import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { ElasticCache } from "@westpierstudio/luma/utils/elastic-cache";
import { Spine } from "pixi-spine";
import { Container } from "pixi.js";

const ANIMATIONS = [`row-1`, `row-2`, `row-3`, `row-4`, `row-5`];

type SparkleWaiter = {
    queued: boolean;
    distance: number;
}

export class ReelSparkles extends View {
    private dragonsSparkleEffects!: ElasticCache<Spine & Container>;
    private pandasSparkleEffects!: ElasticCache<Spine & Container>;
    private sparkleQueue!: SparkleWaiter[];
    private spec!: Record<string, any>;
    private idealReelSpeed!: number;
    private reelsPerSet!: number;

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {resources} = this.services;
        this.spec = resources.getSpec(`reels`);

        this.reelsPerSet = this.services.resources.getSetting(`reels`).reelsPerSet;

        this.idealReelSpeed = this.spec.sparkleEffect.idealReelSpeed;

        this.setupSparkleQueue();
        this.setupSparkleEffects();
    }

    override reset(): void {
        this.setupSparkleQueue();
    }

    private setupSparkleQueue(): void {
        this.sparkleQueue = [];

        for (let i=0; i<8; i++) {
            this.sparkleQueue.push({
                queued: false,
                distance: 0
            });
        }
    }

    private setupSparkleEffects(): void {

        const {factory} = this.services;

        this.dragonsSparkleEffects = new ElasticCache<Spine & Container>(
            () => {
                return factory.createSpine(this.spec.dragonsSparkleEffect, this);
            },
            (sparkle) => {
                this.addChild(sparkle);
            },
            (sparkle) => {
                this.removeChild(sparkle);
                sparkle.state.timeScale = 1;
                sparkle.visible = false;
            }
        );

        this.pandasSparkleEffects = new ElasticCache<Spine & Container>(
            () => {
                return factory.createSpine(this.spec.pandasSparkleEffect, this);
            },
            (sparkle) => {
                this.addChild(sparkle);
            },
            (sparkle) => {
                this.removeChild(sparkle);
                sparkle.state.timeScale = 1;
                sparkle.visible = false;
            }
        );
    }

    createEffect(reelIndex: number, distance: number, reelSpeed?: number | undefined): void {
        if (distance === 5 && reelSpeed) {
            this.playEffect(reelIndex, distance, reelSpeed / this.idealReelSpeed);
        } else {
            this.sparkleQueue[reelIndex].queued = true;
            this.sparkleQueue[reelIndex].distance = distance;
        }
    }

    private playEffect(reelIndex: number, distance: number, speedMultiplier?: number | undefined): void {

        const effect1 = reelIndex < this.reelsPerSet ? this.dragonsSparkleEffects.get() : this.pandasSparkleEffects.get();
        const effect2 = reelIndex < this.reelsPerSet ? this.dragonsSparkleEffects.get() : this.pandasSparkleEffects.get();

        effect1.reference.visible = true;
        effect2.reference.visible = true;

        if (speedMultiplier) {
            effect1.reference.state.timeScale = speedMultiplier;
            effect2.reference.state.timeScale = speedMultiplier;
        }

        if (reelIndex < this.reelsPerSet) {
            effect1.reference.x = this.spec.sparkleEffect.positions.x[reelIndex];
            effect2.reference.x = this.spec.sparkleEffect.positions.x[reelIndex+1];
        } else {
            effect1.reference.x = this.spec.sparkleEffect.positions.x[reelIndex+1];
            effect2.reference.x = this.spec.sparkleEffect.positions.x[reelIndex+2];
        }

        effect1.reference.state.setAnimation(0, ANIMATIONS[distance-1], false);
        effect2.reference.state.setAnimation(0, ANIMATIONS[distance-1], false);

        if (reelIndex < this.reelsPerSet) {
            effect1.reference.state.addListener({ complete: () => {
                this.dragonsSparkleEffects.free(effect1);
            } });
            effect2.reference.state.addListener({ complete: () => {
                this.dragonsSparkleEffects.free(effect2);
            } });
        } else {
            effect1.reference.state.addListener({ complete: () => {
                this.pandasSparkleEffects.free(effect1);
            } });
            effect2.reference.state.addListener({ complete: () => {
                this.pandasSparkleEffects.free(effect2);
            } });
        }
    }

    reelStopped(reelIndex: number): void {

        if (this.sparkleQueue[reelIndex].queued) {

            this.sparkleQueue[reelIndex].queued = false;
            this.playEffect(reelIndex, this.sparkleQueue[reelIndex].distance);
        }
    }
}
